import L from "leaflet";
import type { ProspectImmobileGeo } from "../../api";
import { configureLeafletIcon, readCssToken } from "../address-lookup/map-utils";

export const ITALY_CENTER: [number, number] = [42.5, 12.5];

// Esri World Imagery copre nativamente fino a ~z18 in molte aree italiane; oltre
// il nativo Leaflet up-scala le tile invece di mostrare il grigio "no data".
const TILE_MAX_NATIVE = 18;
const TILE_MAX = 21;
/** Zoom massimo per fit/fly: non oltre il nativo, così resta sempre imagery vera. */
export const FIT_MAX_ZOOM = 18;

/**
 * Aggiunge i base layer (satellite Esri + stradale OSM), l'overlay etichette e
 * un control per cambiare vista. Default attivi: satellite + etichette.
 */
export function addMapBaseLayers(map: L.Map, kind: "satellite" | "catasto" = "satellite"): void {
  if (kind === "catasto") {
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
      maxZoom: TILE_MAX,
    }).addTo(map);
    L.tileLayer.wms("/api/catasto/wms", {
      layers: "fabbricati,CP.CadastralParcel,vestizioni",
      format: "image/png",
      transparent: true,
      version: "1.3.0",
      attribution: "Cartografia catastale © Agenzia delle Entrate",
      minZoom: 15,
      maxZoom: TILE_MAX,
      uppercase: true,
    }).addTo(map);
    return;
  }
  addSatelliteLayers(map);
}

export function addSatelliteLayers(map: L.Map): void {
  const satellite = L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    { attribution: "Imagery © Esri", maxNativeZoom: TILE_MAX_NATIVE, maxZoom: TILE_MAX },
  );
  const street = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: "© OpenStreetMap",
    maxZoom: TILE_MAX,
  });
  const labels = L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    { attribution: "", maxNativeZoom: TILE_MAX_NATIVE, maxZoom: TILE_MAX },
  );

  satellite.addTo(map);
  labels.addTo(map);

  L.control
    .layers(
      { Satellite: satellite, Stradale: street },
      { Etichette: labels },
      { collapsed: true },
    )
    .addTo(map);
}

/** Link Google Maps in vista satellitare dall'alto. */
export function googleMapsSatelliteUrl(lat: number, lon: number): string {
  return "https://www.google.com/maps/@" + lat + "," + lon + ",18z/data=!3m1!1e3";
}

/**
 * Un elemento disegnato sulla mappa, che può essere una particella catastale
 * (poligono) o un pin geocodato dall'indirizzo (marker). `indices` raccoglie
 * tutti gli immobili che condividono la stessa geometria (subalterni / stesso
 * indirizzo); `index` è il primo, restituito al click.
 */
export interface MapEntry {
  kind: "parcel" | "marker";
  /** chiave di dedup: inspireid (parcel) o "lat,lon" (marker). */
  key: string;
  indices: number[];
  polygon?: L.Polygon;
  marker?: L.Marker;
  bounds: L.LatLngBounds;
}

interface ParcelStyles {
  base: L.PathOptions;
  selected: L.PathOptions;
}

/**
 * Stili letti a RUNTIME dai token tema (coerenti con la mappa sorella
 * address-lookup e con dark/light): --map-parcel-primary / --map-parcel-secondary.
 * Pesi/opacità calibrati per restare leggibili sopra l'imagery satellitare densa.
 */
function parcelStyles(): ParcelStyles {
  const base = readCssToken("--map-parcel-primary", "--danger") || "#ef4444";
  const selected = readCssToken("--map-parcel-secondary", "--warn") || "#f59e0b";
  return {
    base: { color: base, weight: 2.5, fillColor: base, fillOpacity: 0.18 },
    selected: { color: selected, weight: 4, fillColor: selected, fillOpacity: 0.32 },
  };
}

/** Escape minimale per i valori interpolati nei tooltip HTML. */
function esc(s: string | null | undefined): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;",
  );
}

/**
 * Disegna gli immobili sulla mappa: poligono per le particelle catastali
 * (deduplicate per inspireid), marker per quelli localizzati via geocoding
 * dell'indirizzo (deduplicati per coordinata). onPick riceve il PRIMO indice
 * immobile associato alla geometria cliccata.
 */
export function drawImmobiliLayer(
  map: L.Map,
  items: ProspectImmobileGeo[],
  onPick: (index: number) => void,
): { group: L.LayerGroup; entries: MapEntry[] } {
  const group = L.layerGroup().addTo(map);
  const styles = parcelStyles();
  const byKey = new Map<string, MapEntry>();

  const pushIndex = (key: string, index: number): boolean => {
    const existing = byKey.get(key);
    if (existing) {
      existing.indices.push(index);
      return false;
    }
    return true;
  };

  for (const it of items) {
    const parcel = it.parcel;
    if (parcel && parcel.polygon_latlon && parcel.polygon_latlon.length >= 3) {
      const key = `p:${parcel.inspireid}`;
      if (!pushIndex(key, it.index)) continue;
      const coords: L.LatLngExpression[] = parcel.polygon_latlon.map(
        ([lat, lon]) => [lat, lon] as [number, number],
      );
      const poly = L.polygon(coords, styles.base);
      poly.bindTooltip(
        `<strong>${esc(parcel.nat_ref || parcel.inspireid)}</strong><br/>foglio ${esc(parcel.foglio)} · particella ${esc(parcel.particella)}`,
        { sticky: true },
      );
      const entry: MapEntry = {
        kind: "parcel",
        key,
        indices: [it.index],
        polygon: poly,
        bounds: poly.getBounds(),
      };
      poly.on("click", () => onPick(entry.indices[0]));
      poly.addTo(group);
      byKey.set(key, entry);
      continue;
    }

    // Marker da geocoding indirizzo (nessuna particella catastale).
    const c = it.centroid;
    if (c) {
      const key = `m:${c.lat.toFixed(6)},${c.lon.toFixed(6)}`;
      if (!pushIndex(key, it.index)) continue;
      const m = L.marker([c.lat, c.lon]);
      const label = it.geocode?.formatted || it.ref.comuneName || "indirizzo geolocalizzato";
      m.bindTooltip(
        `<strong>${esc(label)}</strong><br/>posizione da indirizzo (approssimata)`,
        { sticky: true },
      );
      const entry: MapEntry = {
        kind: "marker",
        key,
        indices: [it.index],
        marker: m,
        bounds: L.latLngBounds([c.lat, c.lon], [c.lat, c.lon]),
      };
      m.on("click", () => onPick(entry.indices[0]));
      m.addTo(group);
      byKey.set(key, entry);
    }
  }

  return { group, entries: Array.from(byKey.values()) };
}

/** Inizializza le icone Leaflet dei marker (URL CDN). Idempotente. */
export function ensureMarkerIcons(): void {
  configureLeafletIcon();
}

/**
 * Evidenzia la geometria dell'immobile selezionato e ci fa zoom/scroll.
 * Per i poligoni cambia stile; per i marker alza lo z-index e apre il tooltip.
 */
export function highlightSelected(
  map: L.Map,
  entries: MapEntry[],
  selectedIndex: number | null,
): void {
  const styles = parcelStyles();
  const selected =
    selectedIndex != null ? entries.find((e) => e.indices.includes(selectedIndex)) ?? null : null;

  for (const e of entries) {
    if (e.kind === "parcel" && e.polygon) {
      if (e === selected) {
        e.polygon.setStyle(styles.selected);
        e.polygon.bringToFront();
      } else {
        e.polygon.setStyle(styles.base);
      }
    } else if (e.kind === "marker" && e.marker) {
      e.marker.setZIndexOffset(e === selected ? 1000 : 0);
    }
  }

  if (!selected) return;
  if (selected.kind === "marker" && selected.marker) {
    map.flyTo(selected.marker.getLatLng(), Math.max(map.getZoom(), 16), { duration: 0.6 });
    selected.marker.openTooltip();
  } else {
    map.flyToBounds(selected.bounds, { maxZoom: FIT_MAX_ZOOM, padding: [40, 40], duration: 0.6 });
  }
}
